import { Country } from '@/data';
import { useLanguage } from '@/contexts/AppContext';

interface CountrySelectorProps {
  selectedCountry: Country;
  showDropdown: boolean;
  onToggleDropdown: () => void;
  countrySearch: string;
  onSearchChange: (value: string) => void;
  filteredCountries: Country[];
  onSelectCountry: (country: Country) => void;
  onCloseDropdown: () => void;
  getCountryName: (country: Country) => string;
}

/**
 * 국가 선택 드롭다운 컴포넌트
 * - 국가 코드 및 플래그 표시
 * - 검색 기능
 * - 국가 목록 렌더링
 */
export function CountrySelector({
  selectedCountry,
  showDropdown,
  onToggleDropdown,
  countrySearch,
  onSearchChange,
  filteredCountries,
  onSelectCountry,
  onCloseDropdown,
  getCountryName,
}: CountrySelectorProps) {
  const { language } = useLanguage();
  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={onToggleDropdown}
          className="flex items-center gap-2 px-3 border-r-2 border-border hover:bg-muted transition-colors h-[46px]"
        >
          <span className="text-[20px]">{selectedCountry.flag}</span>
          <span className="text-foreground text-[15px] font-medium">
            {selectedCountry.code}
          </span>
          <svg
            className={`w-[12px] h-[12px] text-muted-foreground transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 12 12"
          >
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Country Dropdown */}
        {showDropdown && (
          <div className="absolute top-full left-0 mt-2 w-[320px] max-h-[400px] bg-background border-2 border-border rounded-[15px] shadow-2xl z-50 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b-2 border-border sticky top-0 bg-background">
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={
                  language === 'ko'
                    ? '국가 검색...'
                    : language === 'es'
                      ? 'Buscar país...'
                      : 'Search country...'
                }
                className="w-full px-3 py-2 bg-muted text-foreground text-[14px] rounded-[10px] outline-none placeholder:text-muted-foreground"
                autoFocus
              />
            </div>

            {/* Country List */}
            <div className="overflow-y-auto max-h-[340px] scrollbar-hide">
              {filteredCountries.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-[14px]">
                  {language === 'ko'
                    ? '검색 결과가 없습니다'
                    : language === 'es'
                      ? 'No se encontraron resultados'
                      : 'No results found'}
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={`${country.code}-${country.country}`}
                    type="button"
                    onClick={() => onSelectCountry(country)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors ${
                      selectedCountry.code === country.code && selectedCountry.country === country.country
                        ? 'bg-primary/10'
                        : ''
                    }`}
                  >
                    <span className="text-[24px]">{country.flag}</span>
                    <div className="flex-1 text-left">
                      <div className="text-[14px] text-foreground font-medium">
                        {getCountryName(country)}
                      </div>
                      <div className="text-[12px] text-muted-foreground">
                        {country.code}
                      </div>
                    </div>
                    {selectedCountry.code === country.code && selectedCountry.country === country.country && (
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={onCloseDropdown}
        />
      )}
    </>
  );
}
